// ─────────────────────────────────────────────────────────────────────────────
// SAP Insights POC — Filter Bar
// Copy of AutomationFilterBar.tsx, retargeted to useSapFilters()/SapFilterContext.
// Everything else (Days/Weeks/Months toggle, period popover, Apply/validation)
// is unchanged — this component has no automation-specific logic of its own
// in the original, so the only edit needed is the context import.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  SapFilterState,
  TimeFilterType,
  useSapFilters,
  validateSapFilterSelection,
} from '../../contexts/SapFilterContext';
import { getRecentMonthGroups, getRecentWeekGroups, TimePeriodGroup } from '../../utils/automationTimePeriods';

function parseLocalIsoDate(value: string): Date {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? new Date(value) : date;
}

export default function SapFilterBar() {
  const { filters, setFilters } = useSapFilters();

  const [pendingType, setPendingType] = useState<TimeFilterType>(filters.timeFilterType);
  const [pendingSelectedPeriods, setPendingSelectedPeriods] = useState<string[]>(filters.selectedPeriods);
  const [pendingFrom, setPendingFrom] = useState<string>(filters.dateFrom);
  const [pendingTo, setPendingTo] = useState<string>(filters.dateTo);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set([new Date().getFullYear()]));
  const [filterError, setFilterError] = useState<string | null>(null);
  const [periodAnchorEl, setPeriodAnchorEl] = useState<HTMLElement | null>(null);

  const weekGroups = useMemo(() => getRecentWeekGroups(), []);
  const monthGroups = useMemo(() => getRecentMonthGroups(), []);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    setPendingType(filters.timeFilterType);
    setPendingSelectedPeriods(filters.selectedPeriods);
    setPendingFrom(filters.dateFrom);
    setPendingTo(filters.dateTo);
    setFilterError(null);
  }, [filters]);

  useEffect(() => {
    setExpandedYears(new Set([currentYear]));
    setSearchTerm('');
    setFilterError(null);
  }, [pendingType, currentYear]);

  const togglePeriod = useCallback((value: string, checked: boolean) => {
    setPendingSelectedPeriods((previous) => {
      const next = new Set(previous);
      if (checked) next.add(value);
      else next.delete(value);
      return Array.from(next);
    });
  }, []);

  const toggleYear = useCallback((group: TimePeriodGroup, checked: boolean) => {
    const groupValues = group.items.map((item) => item.value);
    setPendingSelectedPeriods((previous) => {
      const next = new Set(previous);
      if (checked) groupValues.forEach((value) => next.add(value));
      else groupValues.forEach((value) => next.delete(value));
      return Array.from(next);
    });
  }, []);

  const toggleYearExpanded = useCallback((year: number) => {
    setExpandedYears((previous) => {
      const next = new Set(previous);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  }, []);

  const getSelectionSummary = useCallback(
    (
      type: TimeFilterType,
      selected: string[],
      weekItems: TimePeriodGroup[],
      monthItems: TimePeriodGroup[],
      from: string,
      to: string
    ) => {
      if (type === 'day') {
        if (!from || !to) return 'Select a date range';
        return `${format(parseLocalIsoDate(from), 'MMM d')} - ${format(parseLocalIsoDate(to), 'MMM d')}`;
      }

      if (selected.length === 0) {
        return type === 'week' ? 'No weeks selected' : 'No months selected';
      }

      const selectedSet = new Set(selected);
      const groups = type === 'week' ? weekItems : monthItems;
      const fullySelectedYears: number[] = [];
      const selectedItems: Array<{ label: string; year: number }> = [];

      groups.forEach((group) => {
        const total = group.items.length;
        const selectedCount = group.items.filter((item) => selectedSet.has(item.value)).length;

        if (selectedCount === 0) return;

        if (selectedCount === total) {
          fullySelectedYears.push(group.year);
          return;
        }

        group.items.forEach((item) => {
          if (selectedSet.has(item.value)) {
            selectedItems.push({ label: item.label, year: item.year });
          }
        });
      });

      if (fullySelectedYears.length === 1 && selectedItems.length === 0) {
        return `${fullySelectedYears[0]} (All)`;
      }

      if (selectedItems.length > 0) {
        const sameYear = selectedItems.every((item) => item.year === selectedItems[0]?.year);
        if (sameYear && selectedItems.length > 3) {
          const noun = type === 'week' ? 'Weeks' : 'Months';
          return `${selectedItems[0].year} (${selectedItems.length} ${noun})`;
        }

        const labels = selectedItems.map((item) => item.label);
        if (labels.length <= 2) return labels.join(', ');
        return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
      }

      if (fullySelectedYears.length > 1) {
        return `${fullySelectedYears[0]} (All), ${fullySelectedYears[1]} (All)`;
      }

      return type === 'week' ? 'No weeks selected' : 'No months selected';
    },
    []
  );

  const handleApply = useCallback(() => {
    const nextSelectedPeriods = pendingType === 'day' ? [] : pendingSelectedPeriods;

    const next: SapFilterState = {
      timeFilterType: pendingType,
      selectedPeriods: nextSelectedPeriods,
      preset: 'custom',
      dateFrom: pendingFrom,
      dateTo: pendingTo,
      label: getSelectionSummary(pendingType, nextSelectedPeriods, weekGroups, monthGroups, pendingFrom, pendingTo),
    };

    const validationError = validateSapFilterSelection(next);
    if (validationError) {
      setFilterError(validationError);
      return;
    }

    setFilterError(null);
    setFilters(next);
    setPeriodAnchorEl(null);
  }, [getSelectionSummary, monthGroups, pendingFrom, pendingSelectedPeriods, pendingTo, pendingType, setFilters, weekGroups]);

  const handleTypeChange = useCallback((_event: React.MouseEvent<HTMLElement>, value: TimeFilterType | null) => {
    if (!value) return;
    setPendingType(value);
    setPendingSelectedPeriods([]);
  }, []);

  const selectionSummary = getSelectionSummary(pendingType, pendingSelectedPeriods, weekGroups, monthGroups, pendingFrom, pendingTo);

  const allGroups = pendingType === 'week' ? weekGroups : monthGroups;
  const filteredGroups = useMemo(
    () =>
      allGroups
        .map((group) => {
          const filteredItems = group.items.filter((item) => {
            if (!searchTerm.trim()) return true;
            const needle = searchTerm.trim().toLowerCase();
            return (
              item.label.toLowerCase().includes(needle) ||
              item.value.toLowerCase().includes(needle) ||
              String(group.year).includes(needle)
            );
          });
          return { ...group, items: filteredItems };
        })
        .filter((group) => group.items.length > 0),
    [allGroups, searchTerm]
  );

  const canApply =
    validateSapFilterSelection({
      timeFilterType: pendingType,
      selectedPeriods: pendingType === 'day' ? [] : pendingSelectedPeriods,
      preset: 'custom',
      dateFrom: pendingFrom,
      dateTo: pendingTo,
      label: selectionSummary,
    } as SapFilterState) === null;

  const periodOverlayOpen = Boolean(periodAnchorEl);

  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={pendingType} onChange={handleTypeChange}>
          <ToggleButton value="day">Days</ToggleButton>
          <ToggleButton value="week">Weeks</ToggleButton>
          <ToggleButton value="month">Months</ToggleButton>
        </ToggleButtonGroup>

        {pendingType === 'day' ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={pendingFrom}
              onChange={(e) => setPendingFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={pendingTo}
              onChange={(e) => setPendingTo(e.target.value)}
            />
          </Stack>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setPeriodAnchorEl(periodOverlayOpen ? null : e.currentTarget)}
            >
              {pendingType === 'week' ? 'Select weeks' : 'Select months'}
            </Button>

            <Popover
              open={periodOverlayOpen}
              anchorEl={periodAnchorEl}
              onClose={() => setPeriodAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ width: 340, maxHeight: 420, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={pendingType === 'week' ? 'Search week or year' : 'Search month or year'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Box>

                <Box sx={{ overflowY: 'auto', p: 1 }}>
                  {filteredGroups.map((group) => {
                    const groupValues = group.items.map((item) => item.value);
                    const selectedCount = groupValues.filter((value) => pendingSelectedPeriods.includes(value)).length;
                    const allChecked = groupValues.length > 0 && selectedCount === groupValues.length;
                    const indeterminate = selectedCount > 0 && selectedCount < groupValues.length;
                    const expanded = expandedYears.has(group.year);

                    return (
                      <Box key={group.year} sx={{ mb: 0.5 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => toggleYearExpanded(group.year)}
                            aria-label={expanded ? `Collapse ${group.year}` : `Expand ${group.year}`}
                          >
                            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                          <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={indeterminate}
                            onChange={(e) => toggleYear(group, e.target.checked)}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {group.year}
                          </Typography>
                        </Stack>

                        {expanded ? (
                          <Stack sx={{ pl: 5 }}>
                            {group.items.map((item) => (
                              <Stack direction="row" alignItems="center" spacing={0.5} key={item.value}>
                                <Checkbox
                                  size="small"
                                  checked={pendingSelectedPeriods.includes(item.value)}
                                  onChange={(e) => togglePeriod(item.value, e.target.checked)}
                                />
                                <Typography variant="body2">{item.label}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        ) : null}
                      </Box>
                    );
                  })}

                  {filteredGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No matching periods found.
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Popover>
          </>
        )}

        <Button variant="contained" size="small" onClick={handleApply} disabled={!canApply}>
          Apply
        </Button>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, minHeight: 20 }}>
        <Typography variant="caption" color="text.secondary">
          {selectionSummary}
        </Typography>
        {filterError ? (
          <Typography variant="caption" color="error">
            {filterError}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}
